import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCog } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, PageHeader } from "./shared";

const empty = { name: "", role: "", phone: "", specialties: "", salary: "", commission_rate: 15, joining_date: "", active: true };

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/staff/all").then(({ data }) => setStaff(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ name: m.name, role: m.role, phone: m.phone || "", specialties: m.specialties || "", salary: m.salary ?? "", commission_rate: m.commission_rate ?? 15, joining_date: m.joining_date || "", active: m.active }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.role) { toast.error("Name and role required"); return; }
    const payload = { ...form, salary: Number(form.salary) || 0, commission_rate: Number(form.commission_rate) || 0 };
    try {
      if (editing) await api.put(`/staff/${editing.id}`, payload);
      else await api.post("/staff", payload);
      toast.success(editing ? "Staff updated" : "Staff added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/staff/${id}`); toast.success("Staff removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="staff-page">
      <PageHeader title="Staff" subtitle="Manage your salon team."
        action={
          <button onClick={openNew} data-testid="add-staff-btn"
            className="btn-lift bg-[#0A3F2F] text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
            <Plus className="w-4 h-4" /> Add Staff
          </button>
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="staff-grid">
        {staff.length === 0 && <p className="text-sm text-[#5C6A63]" data-testid="no-staff">No staff members yet.</p>}
        {staff.map((m) => (
          <div key={m.id} className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm" data-testid={`staff-card-${m.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-full bg-[#0A3F2F] text-[#C5A059] flex items-center justify-center">
                <UserCog className="w-5 h-5" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(m)} data-testid={`edit-staff-${m.id}`} className="text-gray-400 hover:text-[#0A3F2F] transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(m.id)} data-testid={`delete-staff-${m.id}`} className="text-gray-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <h3 className="font-bold text-[#1C1C1C] mt-4">{m.name}</h3>
            <p className="text-sm text-[#C5A059] font-semibold">{m.role}</p>
            <p className="text-xs text-[#5C6A63] mt-2">{m.specialties}</p>
            <p className="text-xs text-[#0A3F2F] font-semibold mt-2">
              Commission {m.commission_rate ?? 15}%{m.salary ? ` · Salary ₹${Number(m.salary).toLocaleString("en-IN")}/mo` : ""}
            </p>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5E7E5]">
              <span className="text-xs text-[#5C6A63]">{m.phone || "No phone"}</span>
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${m.active ? "bg-emerald-600/10 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>
                {m.active ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit Staff" : "Add Staff"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="staff-form">
            <input data-testid="staff-name-input" className={inputCls} placeholder="Name *" value={form.name} onChange={set("name")} />
            <input data-testid="staff-role-input" className={inputCls} placeholder="Role * (e.g., Senior Stylist)" value={form.role} onChange={set("role")} />
            <input data-testid="staff-phone-input" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
            <input data-testid="staff-specialties-input" className={inputCls} placeholder="Specialties (comma separated)" value={form.specialties} onChange={set("specialties")} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="staff-salary-input" type="number" min="0" className={inputCls} placeholder="Monthly salary (₹)" value={form.salary} onChange={set("salary")} />
              <input data-testid="staff-commission-input" type="number" min="0" max="100" className={inputCls} placeholder="Commission %" value={form.commission_rate} onChange={set("commission_rate")} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Joining date</label>
              <input data-testid="staff-joining-input" type="date" className={inputCls} value={form.joining_date} onChange={set("joining_date")} />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#5C6A63]">
              <input type="checkbox" data-testid="staff-active-checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Active
            </label>
            <button type="submit" data-testid="staff-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              {editing ? "Save Changes" : "Add Staff"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
